import {memo, type MouseEvent} from 'react';
import {Play, User} from 'lucide-react';
import {useIntl} from 'react-intl';
import {DsAvatar} from 'ch3-ui-lib';
import {formatTime} from '../../../../utilis/timeUtilis';
import {insightsMessages as m} from '../insightsMessages';
import {lucide, readingMeasure, timeChip} from '../insightsUi';
import type {ResolvedSegment} from './speakers';
import {splitByRanges, type TextRange} from './transcriptSearch';

interface TranscriptRowProps {
    segment: ResolvedSegment;
    index: number;
    active: boolean;
    targeted: boolean;
    canSeek: boolean;
    ranges: readonly TextRange[];
    currentMatchStart: number | null;
    onSeek: (index: number, via: 'row' | 'timestamp') => void;
}

const hasSelection = (): boolean => {
    const selection = window.getSelection?.();
    return Boolean(selection && !selection.isCollapsed && selection.toString().trim());
};

const TranscriptRowView = ({segment, index, active, targeted, canSeek, ranges, currentMatchStart, onSeek}: TranscriptRowProps) => {
    const intl = useIntl();
    const time = formatTime(segment.startTime);
    const parts = splitByRanges(segment.text, ranges, currentMatchStart);

    const onRowClick = (event: MouseEvent<HTMLLIElement>) => {
        if (!canSeek || hasSelection()) return;
        if ((event.target as HTMLElement).closest('button, a')) return;
        onSeek(index, 'row');
    };

    return (
        <li
            data-segment-index={index}
            aria-current={active ? 'true' : undefined}
            onClick={onRowClick}
            className={`group flex gap-3 rounded-control px-2.5 py-2 ${canSeek ? 'cursor-pointer' : ''} ${
                active ? 'bg-brand-tint-subtle' : canSeek ? 'hover:bg-surface-muted' : ''
            } ${targeted ? 'ring-2 ring-inset ring-brand-focus' : ''}`}
        >
            <span className="relative mt-0.5 inline-flex shrink-0">
                <DsAvatar
                    size={28}
                    name={segment.speaker.known ? segment.speaker.name : ''}
                    src={segment.speaker.avatarUrl}
                    palette={segment.speaker.palette}
                    icon={segment.speaker.known ? undefined : <User size={14} aria-hidden="true" className={lucide}/>}
                />
                {canSeek && (
                    <span aria-hidden="true" className="absolute inset-0 hidden items-center justify-center rounded-chip bg-brand text-white group-hover:flex">
                        <Play size={12} className={lucide} fill="currentColor"/>
                    </span>
                )}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex items-center gap-1 text-footnote text-ink-muted">
                    <b className="truncate font-semibold text-ink">{segment.speaker.name}</b>
                    <span aria-hidden="true">·</span>
                    {canSeek ? (
                        <button
                            type="button"
                            onClick={() => onSeek(index, 'timestamp')}
                            aria-label={intl.formatMessage(m.playFrom, {time})}
                            className={timeChip}
                        >
                            {time}
                        </button>
                    ) : (
                        <span className="tabular-nums">{time}</span>
                    )}
                </div>
                <p className={`m-0 ${readingMeasure} whitespace-pre-wrap break-words text-callout font-normal leading-[1.55] ${active ? 'text-brand-ink' : 'text-ink'}`}>
                    {parts.map((part, partIndex) => part.match ? (
                        <mark
                            key={partIndex}
                            data-current-match={part.current ? '' : undefined}
                            className={`rounded-[3px] px-px ${part.current ? 'bg-warning-line text-ink ring-2 ring-warning' : 'bg-warning-tint text-warning-ink'}`}
                        >
                            {part.text}
                        </mark>
                    ) : (
                        <span key={partIndex}>{part.text}</span>
                    ))}
                </p>
            </div>
        </li>
    );
};

export const TranscriptRow = memo(TranscriptRowView);
